/**
 * TanStack Query factory functions + mutations for the Phase 4c admin pages.
 *
 * All admin RPCs are SECURITY DEFINER on the DB side and check `is_admin()` /
 * `is_clinic_owner()` themselves. RLS policies still apply for direct table
 * reads (e.g., the clinic detail page reads from `clinics` directly), so admin
 * is the only role that sees cross-clinic data.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@insolech/db';
import { unwrapEdge } from '../util/edge';

/* ============================================================ */
/*  Clinics                                                     */
/* ============================================================ */

export const adminClinicKeys = {
  all: ['admin', 'clinics'] as const,
  list: (params: ClinicListParams) => [...adminClinicKeys.all, 'list', params] as const,
  detail: (id: string) => [...adminClinicKeys.all, 'detail', id] as const,
  members: (id: string) => [...adminClinicKeys.all, 'members', id] as const,
  invitations: (id: string) => [...adminClinicKeys.all, 'invitations', id] as const,
  podiatrists: (id: string) => [...adminClinicKeys.all, 'podiatrists', id] as const,
};

export interface ClinicListParams {
  search?: string;
  publishedFilter?: 'all' | 'published' | 'unpublished';
  page?: number;
  pageSize?: number;
}

export interface ClinicSummaryRow {
  id: string;
  name: string;
  slug: string;
  city: string | null;
  emirate: string | null;
  country: string;
  phone: string | null;
  email: string | null;
  is_published: boolean;
  is_active: boolean;
  member_count: number;
  total_orders: number;
  last_activity: string | null;
  created_at: string;
  total_count: number;
}

export interface ClinicListResult {
  rows: ClinicSummaryRow[];
  total: number;
  page: number;
  pageSize: number;
}

export function adminClinicListQuery(client: SupabaseClient<Database>, params: ClinicListParams) {
  return {
    queryKey: adminClinicKeys.list(params),
    queryFn: async (): Promise<ClinicListResult> => {
      const pageSize = Math.max(1, Math.min(params.pageSize ?? 25, 100));
      const page = Math.max(0, params.page ?? 0);
      const offset = page * pageSize;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (client.rpc as any)('admin_clinic_summary_list', {
        p_search: params.search ?? null,
        p_published_filter: params.publishedFilter ?? 'all',
        p_limit: pageSize,
        p_offset: offset,
      });
      if (error) throw error;
      const rows = (data ?? []) as ClinicSummaryRow[];
      const total = rows.length > 0 ? Number(rows[0]!.total_count) : 0;
      return { rows, total, page, pageSize };
    },
  };
}

export interface ClinicDetail {
  id: string;
  name: string;
  slug: string;
  legal_name: string | null;
  address: string | null;
  city: string | null;
  emirate: string | null;
  country: string;
  phone: string | null;
  email: string | null;
  website_url: string | null;
  logo_url: string | null;
  google_maps_url: string | null;
  geo_lat: number | null;
  geo_lng: number | null;
  default_language: string;
  is_published: boolean;
  is_active: boolean;
  /** 20270358: patient tag-scan reorders for this clinic. Admin-only; default false. */
  reorders_enabled: boolean;
  notes: string | null;
  // R20 — billing / acquisition fields
  acquired_via: 'direct' | 'agent';
  acquired_by_agent_id: string | null;
  payment_terms_days: number;
  /** Human-readable terms label, e.g. "Net 60" / "Due end of next month". */
  payment_terms_label: string | null;
  billing_notes: string | null;
  /** Zoho Books contact id (entered by admin; powers Zoho invoicing). */
  zoho_contact_id: string | null;
  created_at: string;
  updated_at: string;
}

export function adminClinicDetailQuery(client: SupabaseClient<Database>, id: string) {
  return {
    queryKey: adminClinicKeys.detail(id),
    queryFn: async (): Promise<ClinicDetail | null> => {
      // The select string is correct against the live schema; cast bridges
      // the generated DB types until db:gen-types runs after R20 deploy.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const SELECT = 'id, name, slug, legal_name, address, city, emirate, country, phone, email, website_url, logo_url, google_maps_url, geo_lat, geo_lng, default_language, is_published, is_active, reorders_enabled, notes, acquired_via, acquired_by_agent_id, payment_terms_days, payment_terms_label, billing_notes, zoho_contact_id, created_at, updated_at' as any;
      const { data, error } = await client
        .from('clinics')
        .select(SELECT)
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as ClinicDetail | null) ?? null;
    },
  };
}

/* ============================================================ */
/*  R20 — Per-clinic product price overrides                    */
/*                                                              */
/*  Reads from the existing product_prices table. The existing  */
/*  schema is time-series (effective_from..effective_to); this  */
/*  surface exposes the CURRENTLY-EFFECTIVE override per        */
/*  (clinic, product). The set RPC closes any open row before   */
/*  inserting the new one.                                      */
/* ============================================================ */

export const adminClinicPricesKeys = {
  all: ['admin', 'clinic-prices'] as const,
  forClinic: (clinicId: string) => [...adminClinicPricesKeys.all, clinicId] as const,
};

export interface ClinicPriceRow {
  id: string;
  clinic_id: string;
  product_id: string;
  product_name: string;
  product_sku: string | null;
  /** Catalog default for this product (the row where clinic_id IS NULL). null if none. */
  default_price_aed: number | null;
  /** Override price for this clinic. */
  price_aed: number;
  notes: string | null;
  effective_from: string;
}

export function adminClinicPricesQuery(client: SupabaseClient<Database>, clinicId: string) {
  return {
    queryKey: adminClinicPricesKeys.forClinic(clinicId),
    queryFn: async (): Promise<ClinicPriceRow[]> => {
      // Two queries: clinic overrides (currently effective), and catalog
      // defaults (also currently effective). We then join in JS so the UI
      // can render "default vs override" side by side without a complex
      // PostgREST embed.
      const today = new Date().toISOString().slice(0, 10);
      const [overridesRes, defaultsRes] = await Promise.all([
        client
          .from('product_prices')
          .select('id, product_id, clinic_id, price_aed, notes, effective_from, effective_to, product:products(id, sku, name_en)')
          .eq('clinic_id', clinicId)
          .or(`effective_to.is.null,effective_to.gt.${today}`),
        client
          .from('product_prices')
          .select('product_id, price_aed')
          .is('clinic_id', null)
          .or(`effective_to.is.null,effective_to.gt.${today}`),
      ]);
      if (overridesRes.error) throw overridesRes.error;
      if (defaultsRes.error) throw defaultsRes.error;

      const defaultByProduct = new Map<string, number>();
      for (const d of (defaultsRes.data ?? []) as Array<{ product_id: string; price_aed: number }>) {
        defaultByProduct.set(d.product_id, d.price_aed);
      }

      type OverrideRow = {
        id: string;
        product_id: string;
        clinic_id: string;
        price_aed: number;
        notes: string | null;
        effective_from: string;
        effective_to: string | null;
        product: { id: string; sku: string | null; name_en: string } | null;
      };
      return ((overridesRes.data ?? []) as unknown as OverrideRow[]).map((r) => ({
        id: r.id,
        clinic_id: r.clinic_id,
        product_id: r.product_id,
        product_name: r.product?.name_en ?? '(deleted product)',
        product_sku: r.product?.sku ?? null,
        default_price_aed: defaultByProduct.get(r.product_id) ?? null,
        price_aed: r.price_aed,
        notes: r.notes,
        effective_from: r.effective_from,
      }));
    },
  };
}

export async function adminSetClinicPrice(
  client: SupabaseClient<Database>,
  input: { clinic_id: string; product_id: string; price_aed: number; notes?: string | null },
): Promise<string> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (client.rpc as any)('admin_set_clinic_price', {
    p_clinic_id: input.clinic_id,
    p_product_id: input.product_id,
    p_price_aed: input.price_aed,
    p_notes: input.notes ?? null,
  });
  if (error) throw error;
  return data as string;
}

export async function adminClearClinicPrice(
  client: SupabaseClient<Database>,
  clinicId: string,
  productId: string,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (client.rpc as any)('admin_clear_clinic_price', {
    p_clinic_id: clinicId,
    p_product_id: productId,
  });
  if (error) throw error;
}

/* ============================================================ */
/*  R20 — Users list + role management                          */
/* ============================================================ */

export type UserRole = 'admin' | 'agent' | 'podiatrist' | 'provider';

export const adminUsersKeys = {
  all: ['admin', 'users'] as const,
  list: () => [...adminUsersKeys.all, 'list'] as const,
};

export interface AdminUserRow {
  user_id: string;
  email: string;
  full_name: string;
  avatar_url: string | null;
  language_preference: string;
  roles: UserRole[];
  primary_clinic_id: string | null;
  /**
   * Bug-fix B: when TRUE this user (typically an agent) earns commission
   * AND sees in /agent the orders they themselves placed as podiatrist
   * on clinics they acquired. Default FALSE on the DB side.
   */
  agent_includes_own_orders: boolean;
  created_at: string;
  last_sign_in_at: string | null;
}

export function adminUsersListQuery(client: SupabaseClient<Database>) {
  return {
    queryKey: adminUsersKeys.list(),
    queryFn: async (): Promise<AdminUserRow[]> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (client.rpc as any)('admin_users_list');
      if (error) throw error;
      return (data ?? []) as AdminUserRow[];
    },
  };
}

export async function adminSetUserRole(
  client: SupabaseClient<Database>,
  userId: string,
  role: UserRole,
  grant: boolean,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (client.rpc as any)('admin_set_user_role', {
    p_user_id: userId,
    p_role: role,
    p_grant: grant,
  });
  if (error) throw error;
}

/**
 * Bug-fix B: admin opt-in for "agent sees / earns commission on their own
 * orders". Backed by the admin_set_agent_includes_own_orders RPC which is
 * SECURITY DEFINER + is_admin()-gated server-side; this helper just
 * forwards the call and surfaces any error to the caller.
 */
export async function adminSetAgentIncludesOwnOrders(
  client: SupabaseClient<Database>,
  userId: string,
  value: boolean,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (client.rpc as any)('admin_set_agent_includes_own_orders', {
    p_user_id: userId,
    p_value: value,
  });
  if (error) throw error;
}

/**
 * Provision a brand-new user via the admin-create-user edge function.
 * The function uses the Supabase auth admin API + service role to create
 * auth.users + profiles + user_roles in one shot.
 */
export interface AdminCreateUserInput {
  email: string;
  password: string;
  full_name: string;
  language_preference?: 'en' | 'ar' | 'es';
  roles: UserRole[];
}

export interface AdminCreateUserResult {
  user_id: string;
  email: string;
  full_name: string;
  roles: UserRole[];
  /** Per-role grant errors (rare; auth user + profile already created). */
  grant_errors?: string[];
}

export async function adminCreateUser(
  client: SupabaseClient<Database>,
  input: AdminCreateUserInput,
): Promise<AdminCreateUserResult> {
  const { data, error } = await client.functions.invoke<unknown>(
    'admin-create-user',
    { body: input },
  );
  if (error) {
    // Try to surface the edge function's structured error message
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ctx = (error as any).context;
    if (ctx?.response) {
      try {
        const body = await ctx.response.json();
        if (body?.error?.message) throw new Error(body.error.message);
      } catch {
        /* fall through */
      }
    }
    throw error;
  }
  return unwrapEdge<AdminCreateUserResult>(data);
}

export async function adminDeleteUser(
  client: SupabaseClient<Database>,
  userId: string,
  reason: string,
): Promise<void> {
  const { error } = await client.functions.invoke('delete-user', {
    body: { user_id: userId, reason },
  });
  if (error) throw error;
}

/**
 * Update a user's full name and/or password. Either field may be
 * omitted; at least one must be present.
 */
export async function adminUpdateUser(
  client: SupabaseClient<Database>,
  input: { user_id: string; full_name?: string; password?: string },
): Promise<void> {
  const { error } = await client.functions.invoke('admin-update-user', {
    body: input,
  });
  if (error) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ctx = (error as any).context;
    if (ctx?.response) {
      try {
        const body = await ctx.response.json();
        if (body?.error?.message) throw new Error(body.error.message);
      } catch {
        /* fall through */
      }
    }
    throw error;
  }
}

/* ============================================================ */
/*  R21 — Clinic member add/remove (replaces invitation flow)   */
/* ============================================================ */

export async function adminAddClinicMember(
  client: SupabaseClient<Database>,
  input: { clinic_id: string; user_id: string },
): Promise<string> {
  // Role is always 'member' on new inserts. The 'owner' value still exists in
  // the DB enum and on grandfathered rows (read side), but the UI no longer
  // surfaces the distinction — see ClinicMemberRow.clinic_role.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (client.rpc as any)('admin_add_clinic_member', {
    p_clinic_id: input.clinic_id,
    p_user_id: input.user_id,
    p_role: 'member',
  });
  if (error) throw error;
  return data as string;
}

export async function adminRemoveClinicMember(
  client: SupabaseClient<Database>,
  memberId: string,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (client.rpc as any)('admin_remove_clinic_member', {
    p_member_id: memberId,
  });
  if (error) throw error;
}

export interface ClinicCreateInput {
  name: string;
  slug: string;
  legal_name?: string | null;
  address?: string | null;
  city?: string | null;
  emirate?: string | null;
  country?: string;
  phone?: string | null;
  email?: string | null;
  website_url?: string | null;
  logo_url?: string | null;
  google_maps_url?: string | null;
  geo_lat?: number | null;
  geo_lng?: number | null;
  default_language?: 'en' | 'ar' | 'es';
  is_published?: boolean;
  is_active?: boolean;
  /** 20270358: persisted by admin_update_clinic; admin-only. */
  reorders_enabled?: boolean;
  notes?: string | null;
  // R20 — billing / acquisition (admin-only fields, all optional)
  acquired_via?: 'direct' | 'agent';
  acquired_by_agent_id?: string | null;
  payment_terms_days?: number;
  /** Human-readable terms label. Persisted by admin_update_clinic. */
  payment_terms_label?: string | null;
  billing_notes?: string | null;
  /** Zoho Books contact id. Persisted by admin_update_clinic. */
  zoho_contact_id?: string | null;
}

export async function adminCreateClinic(
  client: SupabaseClient<Database>,
  input: ClinicCreateInput,
): Promise<string> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (client.rpc as any)('admin_create_clinic', { p_payload: input });
  if (error) throw error;
  return data as string;
}

export async function adminUpdateClinic(
  client: SupabaseClient<Database>,
  id: string,
  patch: Partial<ClinicCreateInput>,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (client.rpc as any)('admin_update_clinic', {
    p_id: id,
    p_payload: patch,
  });
  if (error) throw error;
}

/* ============================================================ */
/*  Clinic logo upload                                            */
/* ============================================================ */

export const CLINIC_ASSETS_BUCKET = 'clinic-assets';
export const CLINIC_LOGO_MAX_BYTES = 10 * 1024 * 1024;
export const CLINIC_LOGO_ALLOWED_MIME: readonly string[] = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/svg+xml',
];

/**
 * Upload (or replace) a clinic logo image to the public `clinic-assets`
 * Supabase Storage bucket and write its public URL back to clinics.logo_url.
 * Returns the new public URL.
 */
export async function adminUploadClinicLogo(
  client: SupabaseClient<Database>,
  args: {
    clinicId: string;
    file: File;
    onProgress?: (fraction: number) => void;
  },
): Promise<string> {
  if (args.file.type && !CLINIC_LOGO_ALLOWED_MIME.includes(args.file.type)) {
    throw new Error(`Logo type "${args.file.type}" is not allowed. Use PNG, JPEG, WebP, or SVG.`);
  }
  if (args.file.size > CLINIC_LOGO_MAX_BYTES) {
    throw new Error(`Logo exceeds ${(CLINIC_LOGO_MAX_BYTES / 1024 / 1024).toFixed(0)} MB limit.`);
  }

  const safeName = args.file.name
    .replace(/\.\./g, '_')
    .replace(/[\\/]/g, '_')
    .replace(/[^A-Za-z0-9._-]/g, '_');
  const storagePath = `clinics/${args.clinicId}/logo/${Date.now()}_${safeName}`;

  args.onProgress?.(0.05);
  const { error: upErr } = await client.storage
    .from(CLINIC_ASSETS_BUCKET)
    .upload(storagePath, args.file, {
      cacheControl: '3600',
      upsert: true,
      contentType: args.file.type || 'application/octet-stream',
    });
  if (upErr) throw upErr;
  args.onProgress?.(0.8);

  const { data: pub } = client.storage.from(CLINIC_ASSETS_BUCKET).getPublicUrl(storagePath);
  const publicUrl = pub?.publicUrl ?? '';
  if (!publicUrl) {
    await client.storage.from(CLINIC_ASSETS_BUCKET).remove([storagePath]);
    throw new Error('Could not resolve a public URL for the uploaded logo.');
  }

  try {
    await adminUpdateClinic(client, args.clinicId, { logo_url: publicUrl });
  } catch (e) {
    // Best-effort cleanup of the orphaned object.
    await client.storage.from(CLINIC_ASSETS_BUCKET).remove([storagePath]);
    throw e;
  }
  args.onProgress?.(1);
  return publicUrl;
}

export interface ClinicMemberRow {
  member_id: string;
  user_id: string;
  full_name: string;
  email: string;
  app_role: 'admin' | 'podiatrist' | 'provider' | null;
  clinic_role: 'owner' | 'member';
  is_active: boolean;
  joined_at: string | null;
  invited_at: string | null;
}

export function adminClinicMembersQuery(client: SupabaseClient<Database>, clinicId: string) {
  return {
    queryKey: adminClinicKeys.members(clinicId),
    queryFn: async (): Promise<ClinicMemberRow[]> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (client.rpc as any)('admin_clinic_members_list', {
        p_clinic_id: clinicId,
      });
      if (error) throw error;
      return (data ?? []) as ClinicMemberRow[];
    },
  };
}

export interface ClinicPodiatristOption {
  user_id: string;
  full_name: string;
}

/**
 * Active podiatrists of ONE clinic, for the new-order wizard's admin
 * "Podiatrist for this order" picker (the choice goes to
 * create_order_with_lines as p_podiatrist_id). A user qualifies when they
 * hold an ACTIVE clinic_members row of the clinic AND the podiatrist app role
 * in user_roles; the display name comes from profiles. Sorted by name.
 *
 * Three reads instead of one embed: neither clinic_members.user_id nor
 * user_roles.user_id carries a foreign key to profiles (both point at
 * auth.users), so PostgREST has no path to join them.
 *
 * RLS assumption: the caller is an ADMIN, who reads every row of
 * clinic_members, user_roles and profiles. A clinic member only sees their
 * own memberships and roles, so for them this list would silently shrink to
 * (at most) themselves; the consuming picker is admin-gated upstream, and the
 * RPC refuses the argument from a non-admin anyway.
 */
export function clinicPodiatristsQuery(client: SupabaseClient<Database>, clinicId: string) {
  return {
    queryKey: adminClinicKeys.podiatrists(clinicId),
    queryFn: async (): Promise<ClinicPodiatristOption[]> => {
      const { data: members, error: membersErr } = await client
        .from('clinic_members')
        .select('user_id')
        .eq('clinic_id', clinicId)
        .eq('is_active', true);
      if (membersErr) throw membersErr;
      const memberIds = Array.from(new Set((members ?? []).map((m) => m.user_id)));
      if (memberIds.length === 0) return [];

      const [rolesRes, profilesRes] = await Promise.all([
        client
          .from('user_roles')
          .select('user_id')
          .eq('role', 'podiatrist')
          .in('user_id', memberIds),
        client.from('profiles').select('id, full_name').in('id', memberIds),
      ]);
      if (rolesRes.error) throw rolesRes.error;
      if (profilesRes.error) throw profilesRes.error;

      const podiatristIds = new Set((rolesRes.data ?? []).map((r) => r.user_id));
      return (profilesRes.data ?? [])
        .filter((p) => podiatristIds.has(p.id))
        .map((p) => ({ user_id: p.id, full_name: p.full_name }))
        .sort((a, b) => a.full_name.localeCompare(b.full_name));
    },
  };
}

export interface ClinicInvitationRow {
  id: string;
  email: string;
  role: 'owner' | 'member';
  token: string;
  expires_at: string;
  created_at: string;
  accepted_at: string | null;
}

export function adminClinicInvitationsQuery(client: SupabaseClient<Database>, clinicId: string) {
  return {
    queryKey: adminClinicKeys.invitations(clinicId),
    queryFn: async (): Promise<ClinicInvitationRow[]> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (client.rpc as any)('admin_clinic_pending_invitations', {
        p_clinic_id: clinicId,
      });
      if (error) throw error;
      return (data ?? []) as ClinicInvitationRow[];
    },
  };
}

export async function adminCreateClinicInvitation(
  client: SupabaseClient<Database>,
  args: { clinic_id: string; email: string; role?: 'owner' | 'member' },
): Promise<{ id: string; token: string; expires_at: string }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (client.rpc as any)('admin_create_clinic_invitation', {
    p_clinic_id: args.clinic_id,
    p_email: args.email,
    p_role: args.role ?? 'member',
  });
  if (error) throw error;
  // Postgres `returns table(...)` becomes an array even with one row.
  const row = Array.isArray(data) ? data[0] : data;
  return row as { id: string; token: string; expires_at: string };
}

export async function adminRevokeClinicInvitation(
  client: SupabaseClient<Database>,
  id: string,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (client.rpc as any)('admin_revoke_clinic_invitation', { p_id: id });
  if (error) throw error;
}

/* ============================================================ */
/*  Products                                                     */
/* ============================================================ */

export const adminProductKeys = {
  all: ['admin', 'products'] as const,
  list: (params: AdminProductListParams) => [...adminProductKeys.all, 'list', params] as const,
  detail: (id: string) => [...adminProductKeys.all, 'detail', id] as const,
  prices: (id: string) => [...adminProductKeys.all, 'prices', id] as const,
};

export interface AdminProductListParams {
  search?: string;
  family?: 'all' | 'custom' | 'ots';
  activeFilter?: 'all' | 'active' | 'inactive';
}

export interface AdminProductRow {
  id: string;
  sku: string;
  line_type: string;
  category: string;
  name_en: string;
  name_ar: string | null;
  name_es: string | null;
  description_en: string | null;
  default_image_url: string | null;
  is_active: boolean;
  display_order: number;
  zoho_item_id: string | null;
  created_at: string;
  updated_at: string;
}

const CUSTOM_LINE_TYPES = [
  'custom_insole',
  'premium_3d_insole',
  'orthopedic_sandal',
  'sandal_insole_combo',
];
const OTS_LINE_TYPES = ['ots_insole', 'orthopaedic_material', 'other_equipment'];

export function adminProductListQuery(
  client: SupabaseClient<Database>,
  params: AdminProductListParams,
) {
  return {
    queryKey: adminProductKeys.list(params),
    queryFn: async (): Promise<AdminProductRow[]> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let q: any = client
        .from('products')
        .select(
          'id, sku, line_type, category, name_en, name_ar, name_es, description_en,' +
            ' default_image_url, is_active, display_order, zoho_item_id, created_at, updated_at',
        );
      if (params.search && params.search.trim()) {
        const term = params.search.trim();
        q = q.or(`name_en.ilike.%${term}%,sku.ilike.%${term}%`);
      }
      if (params.family === 'custom') q = q.in('line_type', CUSTOM_LINE_TYPES);
      else if (params.family === 'ots') q = q.in('line_type', OTS_LINE_TYPES);
      if (params.activeFilter === 'active') q = q.eq('is_active', true);
      else if (params.activeFilter === 'inactive') q = q.eq('is_active', false);
      q = q.order('display_order', { ascending: true });
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as AdminProductRow[];
    },
  };
}

export function adminProductDetailQuery(client: SupabaseClient<Database>, id: string) {
  return {
    queryKey: adminProductKeys.detail(id),
    queryFn: async (): Promise<AdminProductRow | null> => {
      const { data, error } = await client
        .from('products')
        .select(
          'id, sku, line_type, category, name_en, name_ar, name_es, description_en,' +
            ' default_image_url, is_active, display_order, zoho_item_id, created_at, updated_at',
        )
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as AdminProductRow | null;
    },
  };
}

export interface AdminProductPriceRow {
  id: string;
  product_id: string;
  clinic_id: string | null;
  price_aed: number;
  effective_from: string;
  effective_to: string | null;
  notes: string | null;
  created_at: string;
  clinic?: { id: string; name: string } | null;
}

export function adminProductPricesQuery(client: SupabaseClient<Database>, productId: string) {
  return {
    queryKey: adminProductKeys.prices(productId),
    queryFn: async (): Promise<AdminProductPriceRow[]> => {
      const { data, error } = await client
        .from('product_prices')
        .select(
          'id, product_id, clinic_id, price_aed, effective_from, effective_to, notes, created_at,' +
            ' clinic:clinics(id, name)',
        )
        .eq('product_id', productId)
        .order('effective_from', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as AdminProductPriceRow[];
    },
  };
}

export interface AdminProductCreateInput {
  sku: string;
  line_type: string;
  category?: string;
  name_en: string;
  name_ar?: string | null;
  name_es?: string | null;
  description_en?: string | null;
  default_image_url?: string | null;
  is_active?: boolean;
  display_order?: number;
  zoho_item_id?: string | null;
}

export async function adminCreateProduct(
  client: SupabaseClient<Database>,
  input: AdminProductCreateInput,
): Promise<string> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (client.rpc as any)('admin_create_product', {
    p_payload: input,
  });
  if (error) throw error;
  return data as string;
}

export async function adminUpdateProduct(
  client: SupabaseClient<Database>,
  id: string,
  patch: Partial<AdminProductCreateInput>,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (client.rpc as any)('admin_update_product', {
    p_id: id,
    p_payload: patch,
  });
  if (error) throw error;
}

export async function adminSetProductPrice(
  client: SupabaseClient<Database>,
  args: { product_id: string; clinic_id: string | null; price_aed: number; notes?: string | null },
): Promise<string> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (client.rpc as any)('admin_set_product_price', {
    p_product_id: args.product_id,
    p_clinic_id: args.clinic_id,
    p_price_aed: args.price_aed,
    p_notes: args.notes ?? null,
  });
  if (error) throw error;
  return data as string;
}

/* ============================================================ */
/*  Providers                                                    */
/* ============================================================ */

export const adminProviderKeys = {
  all: ['admin', 'providers'] as const,
  list: (params: AdminProviderListParams) => [...adminProviderKeys.all, 'list', params] as const,
  detail: (id: string) => [...adminProviderKeys.all, 'detail', id] as const,
};

export interface AdminProviderListParams {
  search?: string;
  activeFilter?: 'all' | 'active' | 'inactive';
}

export type ProviderType =
  | 'eu_end_to_end'
  | 'local_designer'
  | 'local_printer'
  | 'local_workshop';

export const PROVIDER_TYPE_LABELS: Record<ProviderType, string> = {
  eu_end_to_end: 'EU end-to-end',
  local_designer: 'Local: designer',
  local_printer: 'Local: printer',
  local_workshop: 'Local: workshop',
};

export interface AdminProviderRow {
  id: string;
  name: string;
  contact_email: string | null;
  ordering_email: string | null;
  contact_phone: string | null;
  notes: string | null;
  is_active: boolean;
  /** NULL for non-production suppliers: they have no manufacturing role and
   *  since 20270350 the column stopped forcing one. */
  provider_type: ProviderType | null;
  zoho_contact_id: string | null;
  currency: string | null;
  payment_terms_days: number | null;
  payment_terms_label: string | null;
  billing_notes: string | null;
  country_code: string | null;
  tax_treatment: string | null;
  tax_reg_no: string | null;
  is_production: boolean;
  total_assigned: number;
  last_assigned_at: string | null;
  user_link_count: number;
  created_at: string;
}

export function adminProviderListQuery(
  client: SupabaseClient<Database>,
  params: AdminProviderListParams,
) {
  return {
    queryKey: adminProviderKeys.list(params),
    queryFn: async (): Promise<AdminProviderRow[]> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (client.rpc as any)('admin_provider_summary_list', {
        p_search: params.search ?? null,
        p_active_filter: params.activeFilter ?? 'all',
      });
      if (error) throw error;
      return (data ?? []) as AdminProviderRow[];
    },
  };
}

export interface AdminProviderInput {
  name: string;
  contact_email?: string | null;
  /** Supplier ordering address (Phase 3 draft-email "to"), separate from contact_email. */
  ordering_email?: string | null;
  contact_phone?: string | null;
  notes?: string | null;
  is_active?: boolean;
  provider_type?: ProviderType;
  /** Billing fields (Phase 1) - mirror clinics. Persisted by the provider RPCs. */
  zoho_contact_id?: string | null;
  currency?: string | null;
  payment_terms_days?: number | null;
  payment_terms_label?: string | null;
  billing_notes?: string | null;
  /** ISO 3166-1 alpha-2. Drives whether a bill is domestic or an import. */
  country_code?: string | null;
  /** Zoho Books GCC tax_treatment, mirrored from the Zoho contact. */
  tax_treatment?: string | null;
  /** Supplier TRN. Legitimately empty for a non_gcc supplier. */
  tax_reg_no?: string | null;
  /** False for suppliers that exist only for invoice capture (packaging,
   *  transport). Refused by the production assignment paths at the DB level. */
  is_production?: boolean;
}

export async function adminCreateProvider(
  client: SupabaseClient<Database>,
  input: AdminProviderInput,
): Promise<string> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (client.rpc as any)('admin_create_provider', {
    p_payload: input,
  });
  if (error) throw error;
  return data as string;
}

export async function adminUpdateProvider(
  client: SupabaseClient<Database>,
  id: string,
  patch: Partial<AdminProviderInput>,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (client.rpc as any)('admin_update_provider', {
    p_id: id,
    p_payload: patch,
  });
  if (error) throw error;
}

/**
 * Pull a supplier's tax data back from Zoho, which is where it is maintained.
 *
 * Exists so a tax change made in Zoho does not need a developer to reach the
 * platform. The edge function is read-only against Zoho and never overwrites a
 * platform value with an empty Zoho one, so a blank in Zoho cannot silently
 * erase a correct treatment here.
 */
export async function adminResyncProviderFromZoho(
  client: SupabaseClient<Database>,
  providerId: string,
): Promise<{
  changed: string[];
  zoho: {
    contact_name: string | null;
    tax_treatment: string | null;
    tax_reg_no: string | null;
    country: string | null;
    country_code: string | null;
  };
}> {
  const { data, error } = await client.functions.invoke<unknown>(
    'resync-provider-from-zoho',
    { body: { provider_id: providerId } },
  );
  if (error) {
    // Same reasoning as pushVendorInvoiceToZoho: supabase-js hides the real
    // message in error.context on any non-2xx.
    const ctx = (error as { context?: { text?: () => Promise<string> } }).context;
    if (ctx && typeof ctx.text === 'function') {
      try {
        const body = await ctx.text();
        const parsed = JSON.parse(body) as { error?: { message?: string } };
        if (parsed?.error?.message) throw new Error(parsed.error.message);
      } catch (inner) {
        if (inner instanceof Error && inner.message && !(inner instanceof SyntaxError)) throw inner;
      }
    }
    throw error;
  }
  const payload = (data as { data?: unknown })?.data ?? data;
  return payload as {
    changed: string[];
    zoho: {
      contact_name: string | null;
      tax_treatment: string | null;
      tax_reg_no: string | null;
      country: string | null;
      country_code: string | null;
    };
  };
}

/* ============================================================ */
/*  Email templates                                              */
/* ============================================================ */

export const adminEmailTemplateKeys = {
  all: ['admin', 'email-templates'] as const,
  list: (clinicId: string | null) => [...adminEmailTemplateKeys.all, 'list', clinicId] as const,
};

export interface EmailTemplateRow {
  id: string;
  template_key: string;
  language: string;
  clinic_id: string | null;
  subject: string;
  body_html: string | null;
  body_variables: string[] | null;
  is_active: boolean;
  updated_at: string;
  /** Non-null = a live sender depends on this row; deletion is refused by
   *  a BEFORE DELETE trigger (migration 20270331000000). */
  protected_reason?: string | null;
}

export function adminEmailTemplateListQuery(
  client: SupabaseClient<Database>,
  clinicId: string | null = null,
) {
  return {
    queryKey: adminEmailTemplateKeys.list(clinicId),
    queryFn: async (): Promise<EmailTemplateRow[]> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (client.rpc as any)('admin_email_templates_list', {
        p_clinic_id: clinicId,
      });
      if (error) throw error;
      return (data ?? []) as EmailTemplateRow[];
    },
  };
}

export interface EmailTemplateUpsertInput {
  template_key: string;
  language: 'en' | 'ar' | 'es';
  clinic_id?: string | null;
  subject: string;
  body_html?: string | null;
  body_variables?: string[];
  is_active?: boolean;
}

export async function adminUpsertEmailTemplate(
  client: SupabaseClient<Database>,
  input: EmailTemplateUpsertInput,
): Promise<string> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (client.rpc as any)('admin_upsert_email_template', {
    p_payload: input,
  });
  if (error) throw error;
  return data as string;
}

/* ============================================================ */
/*  Marketplace stock (used by both /orders/new and admin views) */
/* ============================================================ */

export const marketplaceKeys = {
  all: ['marketplace'] as const,
  byClinic: (id: string) => [...marketplaceKeys.all, 'clinic', id] as const,
};

export interface MarketplaceStockRow {
  product_id: string;
  sku: string;
  name_en: string;
  description_en: string | null;
  default_image_url: string | null;
  line_type: string;
  category: string;
  size: string;
  quantity_on_hand: number;
  reorder_threshold: number;
  unit_price_aed: number;
  low_stock: boolean;
  out_of_stock: boolean;
}

export function marketplaceStockQuery(client: SupabaseClient<Database>, clinicId: string) {
  return {
    queryKey: marketplaceKeys.byClinic(clinicId),
    queryFn: async (): Promise<MarketplaceStockRow[]> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (client.rpc as any)('clinic_marketplace_stock', {
        p_clinic_id: clinicId,
      });
      if (error) throw error;
      return (data ?? []) as MarketplaceStockRow[];
    },
  };
}

/**
 * Delete an email template (migration 20270331000000). Admin-gated in the
 * RPC; a row whose protected_reason is set is refused by a BEFORE DELETE
 * trigger, so the guard holds even against a direct PostgREST call.
 */
export async function adminDeleteEmailTemplate(
  client: SupabaseClient<Database>,
  id: string,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (client.rpc as any)('admin_delete_email_template', { p_id: id });
  if (error) throw error;
}
